/**
 * Slack Action Handlers
 *
 * Implements handlers for Slack-related actions.
 * Handles sending entities to Slack and sales channels.
 * 
 * NOTE: These handlers call API routes for server-side Slack operations.
 * Client-side code cannot directly use @slack/web-api or other server-only dependencies.
 */

import type { UniversalActionHandlers } from "./types";
import type { DetailActionContext, BaseEntity } from "../types/action-types";

export const slackActions: UniversalActionHandlers = {
  /**
   * Send entity to Slack - opens modal for channel selection
   */
  sendToSlack: async (item: BaseEntity, context: DetailActionContext<BaseEntity>) => {
    context.onOpenModal("send-to-slack", {
      item,
      entityType: context.pageConfig?.entityName,
    });
  },

  /**
   * Open the customer's Slack channel in a new tab.
   *
   * The customer's channel lives in `companies.slack_channel_id`; non-company
   * entities resolve their company server-side. We can't resolve client-side
   * (the resolver is server-only) and the loaded item rarely carries the
   * company's channel id — so we ask the channel-link route, which returns a
   * `slack://`-equivalent deep link. On no channel (404) we toast instead of
   * opening a dead tab. Availability is checked at click time because the
   * action's sync `visible`/`disabled` predicates cannot await this lookup.
   */
  openSlackChannel: async (item: BaseEntity, context: DetailActionContext<BaseEntity>) => {
    const entityType =
      context.pageConfig?.entityName ?? context.surfaceEntityType ?? context.entityName;
    if (!entityType) {
      context.onToast("Unable to determine entity type", "error");
      return;
    }

    const entityId = item.id;
    if (!entityId) {
      context.onToast("Unable to determine record id", "error");
      return;
    }

    try {
      const params = new URLSearchParams({ entityType, entityId });
      const response = await fetch(`/api/slack/channel-link?${params.toString()}`, {
        method: "GET",
      });

      if (response.status === 404) {
        context.onToast("No Slack channel set for this customer", "warning");
        return;
      }

      const data = (await response.json()) as { url?: string; error?: string };

      if (!response.ok || !data.url) {
        context.onToast(
          data.error ? `Failed to open Slack channel: ${data.error}` : "Failed to open Slack channel",
          "error"
        );
        return;
      }

      window.open(data.url, "_blank", "noopener,noreferrer");
      context.onToast("Opening Slack channel…", "info");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      context.onToast(`Failed to open Slack channel: ${message}`, "error");
    }
  },

  /**
   * Send entity to sales channel - direct send without modal
   * Calls API route to handle server-side Slack operations
   */
  sendToSalesChannel: async (item: BaseEntity, context: DetailActionContext<BaseEntity>) => {
    try {
      const entityType = context.pageConfig?.entityName;
      if (!entityType) {
        context.onToast("Unable to determine entity type", "error");
        return;
      }

      // Call API route for server-side Slack operation
      const response = await fetch("/api/slack/send-to-sales-channel", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          entity: item,
          entityType,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        context.onToast(`Failed to send: ${data.error || "Unknown error"}`, "error");
        return;
      }

      context.onToast("Sent to sales channel", "success");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      context.onToast(`Error: ${errorMessage}`, "error");
    }
  },
};
